'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { LEAGUES_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import { formatCurrency } from '@pickleballinc/ui/components/global/utils/formatCurrency';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { ModuleCard } from './ModuleCard';
import { ModuleCardBody } from './ModuleCardBody';

export function renderPlayerLeagueCardLabels(league: LeagueCardProps): string {
	if (league.isCanceled) {
		return 'canceled';
	}

	if (league.isLocked) {
		return 'completed';
	}

	if (league.isRegistrationClosed) {
		return 'registration-closed';
	}

	return parseFloat(league.price) > 0 ? 'price' : 'free';
}

export type UseFavoriteState = (
	id: string,
	initialIsFavorite?: boolean
) => {
	isFavorite: boolean;
	isFavoriteDisabled: boolean;
	isUserLoggedIn: boolean;
	onToggle: (event: React.MouseEvent, id?: string, isFavourite?: boolean | null) => void;
};

const useDefaultFavoriteState: UseFavoriteState = () => ({
	isFavorite: false,
	isFavoriteDisabled: true,
	isUserLoggedIn: false,
	onToggle: () => {}
});

export interface LeagueCardProps {
	id: string;
	slug?: string;
	dateFrom?: string;
	dateTo?: string;
	startTime?: string;
	isCanceled?: boolean;
	isLocked?: boolean;
	isRegistrationClosed?: boolean;
	currency: string;
	formatTag: string;
	location: string;
	logo: string;
	price: string;
	registrationCount: number;
	title: string;
	clubTitle?: string;
	status?: string;
	isAdvertiseOnly?: boolean;
	hideRegisteredPlayers?: boolean;
	shouldHideStartTime?: boolean;
	className?: string;
	logoClassName?: string;
	leaguesAppBaseURL: string;
	customCardHref?: string;
	hideCardHref?: boolean;
	openInNewTab?: boolean;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	showFavoriteIcon?: boolean;
	useFavoriteState?: UseFavoriteState;
	initialIsFavorite?: boolean;
	variant?: 'light' | 'dark';
}

export const LeagueCardv2 = (props: LeagueCardProps) => {
	const { id, slug, customCardHref, leaguesAppBaseURL, status, hideCardHref = false } = props;

	const newStatus = status ? status : renderPlayerLeagueCardLabels(props);

	const href = hideCardHref ? undefined : customCardHref ? customCardHref : `${leaguesAppBaseURL}/leagues/${slug ? slug : id}`;

	return <LeagueCardComponent {...props} status={newStatus} href={href} />;
};

const LeagueCardComponent = ({
	id,
	dateFrom,
	dateTo,
	startTime,
	logo,
	currency,
	formatTag,
	location,
	price,
	registrationCount,
	status,
	title,
	hideRegisteredPlayers,
	isAdvertiseOnly,
	shouldHideStartTime,
	className,
	logoClassName,
	href,
	openInNewTab,
	nativeAnchor,
	showFavoriteIcon,
	initialIsFavorite,
	variant = 'light',
	useFavoriteState
}: LeagueCardProps & { href?: string }) => {
	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const getImageToDisplay = useMemo(() => {
		if (logo && (logo.toLowerCase().includes('/uploads/clubs/') || logo.toLowerCase().includes('/uploads/events/'))) {
			return getCDNURL(logo, 566, 384);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(LEAGUES_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	const images = [getImageToDisplay];

	const formattedFromDate = dateFrom ? formatDateTime(dateFrom, { parseZone: true }) : null;
	const formattedToDate = dateTo ? formatDateTime(dateTo, { parseZone: true }) : null;

	const statusLabel =
		status === 'completed'
			? 'Completed'
			: status === 'registration-closed'
				? 'View'
				: status === 'price'
					? formatCurrency(price, currency, formatTag)
					: status === 'free'
						? 'Register'
						: null;

	const playersLabel = !hideRegisteredPlayers
		? isAdvertiseOnly
			? 'Advertised'
			: `${registrationCount} player${registrationCount != 1 ? 's' : ''}`
		: null;

	const showFooterRow = Boolean(status || registrationCount);

	/*
	 * `shouldHideStartTime` has to OMIT the entry, not blank its content. `ModuleCardBody` maps every
	 * metaLine to a div with `minHeight: metaLineMinHeight` so rows stay aligned across a grid of
	 * leagues — a null-content entry therefore still costs its 20px, plus one more 8px `gap-2`. That
	 * made this card 28px taller than `TournamentCardv2` (which has a single metaLine) wherever the two
	 * sit in the same row, e.g. the HP marketing listing-ad rotator on the tournament search page.
	 *
	 * Callers that don't pass the flag are unaffected: the row is still reserved when `startTime` is
	 * empty, which is what keeps a grid of league cards aligned.
	 */
	const metaLines = [
		{ id: 'date-range', content: formattedFromDate && formattedToDate ? `${formattedFromDate} - ${formattedToDate}` : null },
		...(shouldHideStartTime ? [] : [{ id: 'start-time', content: startTime }])
	];

	const { isFavorite, isFavoriteDisabled, isUserLoggedIn, onToggle } = (useFavoriteState ?? useDefaultFavoriteState)(id, initialIsFavorite);

	/*
	 * No `mt-auto` on the inner wrapper below: it is the only child of this column flex, so it only ever
	 * bottom-aligned the card when the card was shorter than its cell — precisely the HP marketing ad
	 * case (one fewer reserved meta row than a host league card). Top alignment keeps the card images on
	 * a line across the row. A card that fills its cell is unaffected either way.
	 */
	return (
		<div className="flex flex-col h-full gap-2">
			<div className="relative min-[500px]:min-w-[224px] min-[500px]:max-w-[286px]">
				<ModuleCard
					id={id}
					images={images}
					imageAlt={title}
					isPlaceholderImage={isPlaceholderImage}
					href={href}
					target={openInNewTab ? '_blank' : '_self'}
					nativeAnchor={nativeAnchor}
					className={className}
					imageContainerClassName={logoClassName}
					imageClassName={clsx(logoClassName)}
					showFavoriteIcon={showFavoriteIcon}
					isUserLoggedIn={isUserLoggedIn}
					isFavourite={isFavorite}
					isFavoriteDisabled={isFavoriteDisabled}
					onFavoriteToggle={onToggle}
				>
					<ModuleCardBody
						title={title}
						subtitle={location}
						metaLines={metaLines}
						footerLeft={showFooterRow ? statusLabel : null}
						footerLeftIsPrice={status === 'price'}
						footerRight={showFooterRow ? playersLabel : null}
						variant={variant}
					/>
				</ModuleCard>
			</div>
		</div>
	);
};
